// ─────────────────────────────────────────────────────────────────────────────
// SplashScreen.jsx — Phase 2
// First screen the user sees. Determines role before the app loads.
//
// Adaptive single-field approach:
//   User types an email  → password field appears → trainer login / register
//   User types a code    → student section appears (join exercise)
//   User clicks Guest    → guest access (learning mode, no exercise)
//
// On mount: checks for an existing Supabase session. If found, skips this
// screen entirely and resolves as TRAINER automatically.
//
// Props:
//   onResolved(role, session) — called when identity is confirmed
//     role:    'TRAINER' | 'STUDENT' | 'GUEST'
//     session: { userName, trainerId?, studentId?, exerciseId?, sessionCode? }
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Collapse,
  Link,
  InputAdornment,
  IconButton,
  Chip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SchoolIcon from '@mui/icons-material/School';

import { authService }          from '../services/authService';
import { exerciseService }      from '../services/exerciseService'; // P2-S3
import { cloudStorageService }  from '../services/cloudStorageService'; // TRAINER-GATE-001

// Matches personal codes like ALPHA-12, BRAVO-034, CHARLIE-7
const CODE_PATTERN = /^[A-Z]{2,}-\d+$/i;

// ── SF-1: Student session persistence across page refresh ────────────────────
// Stored in localStorage with an 8-hour TTL. Cleared automatically on expiry
// or overwritten on a new successful join. Trainers are never affected.
const _STUDENT_SESSION_KEY = 'psp_student_session';
const _STUDENT_SESSION_TTL = 8 * 3600 * 1000; // 8 hours in ms

function _saveStudentSession(data) {
  try {
    localStorage.setItem(_STUDENT_SESSION_KEY, JSON.stringify({ ...data, savedAt: Date.now() }));
  } catch (_) {} // silent — storage quota etc.
}

function _restoreStudentSession() {
  try {
    const raw = localStorage.getItem(_STUDENT_SESSION_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    if (!stored?.studentId || !stored?.exerciseId) return null;
    if (Date.now() - (stored.savedAt || 0) > _STUDENT_SESSION_TTL) {
      localStorage.removeItem(_STUDENT_SESSION_KEY); // expired — clean up
      return null;
    }
    return stored;
  } catch (_) {
    return null;
  }
}

// ── View states ──────────────────────────────────────────────────────────────
const VIEW = {
  IDENTIFY:               'IDENTIFY',               // initial — just the ID field
  TRAINER_LOGIN:          'TRAINER_LOGIN',           // email detected → password field shown
  TRAINER_REG:            'TRAINER_REG',             // "Create account" clicked
  STUDENT_JOIN:           'STUDENT_JOIN',            // code detected → session code field shown
  FORGOT_PASSWORD:        'FORGOT_PASSWORD',         // "Forgot password?" clicked
  RESET_PENDING:          'RESET_PENDING',           // P2-S6: 24h delay — reset queued, return later
  RESET_SENT:             'RESET_SENT',              // reset email sent — show confirmation
  STUDENT_SELF_REGISTER:  'STUDENT_SELF_REGISTER',  // P3: no personal code → register with session code
  STUDENT_CODE_ASSIGNED:  'STUDENT_CODE_ASSIGNED',  // P3: personal code issued — confirm before joining
};

export default function SplashScreen({ onResolved }) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [checkingSession, setCheckingSession] = useState(true);
  const [view, setView]                       = useState(VIEW.IDENTIFY);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState(null);
  const [showPassword, setShowPassword]       = useState(false);
  // INVITE-FIX: true when user arrives via Supabase admin invite magic link
  const [isInvitedUser, setIsInvitedUser]     = useState(false);

  // Shared field
  const [idValue, setIdValue] = useState('');

  // Trainer login
  const [password, setPassword] = useState('');

  // Trainer registration
  const [regName,     setRegName]     = useState('');
  const [regEmail,    setRegEmail]    = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm,  setRegConfirm]  = useState('');
  const [regOrg,      setRegOrg]      = useState('');

  // Student join
  const [sessionCode, setSessionCode] = useState('');

  // P3: Student self-registration
  const [selfRegSessionCode,   setSelfRegSessionCode]   = useState('');
  const [selfRegName,          setSelfRegName]          = useState('');
  const [selfRegRank,          setSelfRegRank]          = useState('');
  const [selfRegCountry,       setSelfRegCountry]       = useState('');
  const [pendingStudentSession, setPendingStudentSession] = useState(null);

  // Forgot password
  const [resetEmail,   setResetEmail]   = useState('');
  const [pendingReset, setPendingReset] = useState(null); // P2-S6: { id, hours_remaining, email }

  // TRAINER-GATE-001: trainer invite token — controls "Create account" visibility
  const [trainerInviteToken,    setTrainerInviteToken]    = useState(null);
  const [trainerInviteValid,    setTrainerInviteValid]    = useState(false);
  const [trainerInviteChecking, setTrainerInviteChecking] = useState(false);

  // ── On mount: restore existing trainer or student session ───────────────
  useEffect(() => {
    // SF-2: Pre-fill session code if trainer shared a QR link (?session=XXX)
    const urlParams   = new URLSearchParams(window.location.search);
    const qrSession   = urlParams.get('session');
    if (qrSession) {
      const cleaned = qrSession.trim().toUpperCase();
      setSessionCode(cleaned);
      setView(VIEW.STUDENT_JOIN);
      // Strip the param from the URL without a page reload
      const cleanUrl = window.location.pathname;
      window.history.replaceState(null, '', cleanUrl);
    }

    // TRAINER-GATE-001: detect ?trainer_invite=TOKEN for new trainer registration
    const tiParam = urlParams.get('trainer_invite');
    if (tiParam) {
      setTrainerInviteToken(tiParam);
      sessionStorage.setItem('psp_pending_trainer_invite', tiParam);
      setTrainerInviteChecking(true);
      // Strip the token from the URL bar (it's already stored in sessionStorage)
      const cleanUrl = window.location.pathname;
      window.history.replaceState(null, '', cleanUrl);
      // Validate token via anon RPC (works before authentication)
      cloudStorageService.validateTrainerInvite(tiParam)
        .then(r => {
          const isValid = r?.valid === true;
          setTrainerInviteValid(isValid);
          // Jump straight to the registration form — no need to type email first
          if (isValid) setView(VIEW.TRAINER_REG);
        })
        .catch(() => setTrainerInviteValid(false))
        .finally(() => setTrainerInviteChecking(false));
    } else {
      // Restore any pending invite from sessionStorage (e.g. after page refresh)
      const storedTI = sessionStorage.getItem('psp_pending_trainer_invite');
      if (storedTI) {
        setTrainerInviteToken(storedTI);
        setTrainerInviteChecking(true);
        cloudStorageService.validateTrainerInvite(storedTI)
          .then(r => {
            const isValid = r?.valid === true;
            setTrainerInviteValid(isValid);
            if (isValid) setView(VIEW.TRAINER_REG);
          })
          .catch(() => setTrainerInviteValid(false))
          .finally(() => setTrainerInviteChecking(false));
      }
    }

    authService.getSession()
      .then(session => {
        // INVITE-FIX: Detect a fresh Supabase admin-invite redirect.
        // Supabase appends `type=invite` to the URL hash after processing the
        // magic link token. If present, the user just clicked an invite email —
        // show the splash with a welcome banner instead of auto-skipping to the app.
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        const isFreshInvite = hashParams.get('type') === 'invite';
        if (isFreshInvite) {
          // Clean the hash — supabase-js already consumed the token
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          setIsInvitedUser(true);
          setIdValue(session?.user?.email || '');
          setCheckingSession(false);
          return; // do NOT call onResolved — user must confirm via "Continue" button
        }

        if (session?.user) {
          // Active Supabase session found (returning user) — skip splash entirely
          onResolved('TRAINER', {
            trainerId: session.user.id,
            userName:  authService.getDisplayName(session.user),
          });
        } else {
          // SF-1: No trainer session — try restoring a saved student session
          const saved = _restoreStudentSession();
          if (saved) {
            onResolved('STUDENT', saved);
          } else {
            setCheckingSession(false);
          }
        }
      })
      .catch(() => {
        // SF-1: On auth error, still try student session restore
        const saved = _restoreStudentSession();
        if (saved) {
          onResolved('STUDENT', saved);
        } else {
          setCheckingSession(false);
        }
      });
  }, [onResolved]);

  // ── INVITE-FIX: Continue button for freshly invited users ────────────────
  // The Supabase session is already established by the magic link — just resolve.
  const handleInvitedContinue = useCallback(async () => {
    setLoading(true);
    try {
      const session = await authService.getSession();
      if (session?.user) {
        onResolved('TRAINER', {
          trainerId: session.user.id,
          userName:  authService.getDisplayName(session.user),
        });
      } else {
        setError('Session expired — please log in manually.');
        setIsInvitedUser(false);
      }
    } catch (_) {
      setError('Could not resume session — please log in manually.');
      setIsInvitedUser(false);
    } finally {
      setLoading(false);
    }
  }, [onResolved]);

  // ── ID field change — detect type ─────────────────────────────────────────
  const handleIdChange = useCallback((e) => {
    const val = e.target.value;
    setIdValue(val);
    setError(null);

    if (val.includes('@')) {
      setView(VIEW.TRAINER_LOGIN);
    } else if (CODE_PATTERN.test(val.trim())) {
      setView(VIEW.STUDENT_JOIN);
    } else {
      setView(VIEW.IDENTIFY);
    }
  }, []);

  // ── Trainer login ─────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!idValue.trim() || !password) return;
    setLoading(true);
    setError(null);
    try {
      const data = await authService.login({ email: idValue.trim(), password });

      // TRAINER-GATE-001: redeem pending trainer invite first (covers "existing student
      // being upgraded to trainer" case — invite sets is_trainer=true before the check)
      const pendingTI = sessionStorage.getItem('psp_pending_trainer_invite');
      if (pendingTI) {
        try {
          await cloudStorageService.redeemTrainerInvite(pendingTI);
          sessionStorage.removeItem('psp_pending_trainer_invite');
          setTrainerInviteToken(null);
          setTrainerInviteValid(false);
        } catch (_) { /* is_trainer check below will catch failures */ }
      }

      // TRAINER-GATE-001: verify is_trainer or is_admin before granting trainer access
      const profile = await cloudStorageService.getProfile();
      if (!profile?.is_trainer && !profile?.is_admin) {
        await authService.logout();
        setError(
          'This account is not authorised as a trainer. ' +
          'To join an exercise as a student, use the QR code or join link from your exercise. ' +
          'To become a trainer, ask your administrator for an invite link.'
        );
        return;
      }

      onResolved('TRAINER', {
        trainerId: data.user.id,
        userName:  authService.getDisplayName(data.user),
      });
    } catch (err) {
      // INVITE-FIX: When an admin invited this email via Dashboard, the trainer
      // has no password yet. Guide them to Forgot Password to set one.
      setError(
        err.message?.includes('Invalid')
          ? 'Incorrect email or password. If you were invited by an administrator and have not yet set a password, use "Forgot password?" below.'
          : (err.message || 'Login failed. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Trainer registration ──────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e?.preventDefault();
    if (!regName.trim() || !regEmail.trim() || !regPassword) return;
    if (regPassword !== regConfirm) {
      setError('Passwords do not match.');
      return;
    }
    if (regPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await authService.register({
        email:        regEmail.trim(),
        password:     regPassword,
        fullName:     regName.trim(),
        organisation: regOrg.trim(),
      });
      if (data.session) {
        // TRAINER-GATE-001: redeem pending trainer invite (sets is_trainer=true on profile)
        const pendingTI = sessionStorage.getItem('psp_pending_trainer_invite');
        if (pendingTI) {
          try {
            await cloudStorageService.redeemTrainerInvite(pendingTI);
            sessionStorage.removeItem('psp_pending_trainer_invite');
            setTrainerInviteToken(null);
            setTrainerInviteValid(false);
          } catch (inviteErr) {
            console.warn('Trainer invite redemption failed:', inviteErr.message);
          }
        }
        // Email confirmation is OFF — session created immediately
        onResolved('TRAINER', {
          trainerId: data.user.id,
          userName:  regName.trim(),
        });
      } else {
        // Email confirmation is ON — should not happen with recommended settings
        setError(
          'Account created. Please check your email to confirm, then log in.'
        );
        setView(VIEW.TRAINER_LOGIN);
        setIdValue(regEmail.trim());
      }
    } catch (err) {
      // INVITE-FIX: Supabase returns "User already registered" when the admin
      // invited this email via the Dashboard (which pre-creates the auth.users row).
      // Guide the trainer to use Forgot Password to set their password and log in.
      const alreadyExists = /already registered|already been registered|already exists/i.test(err.message || '');
      if (alreadyExists) {
        setView(VIEW.TRAINER_LOGIN);
        setIdValue(regEmail.trim());
        setError(
          'An account with this email already exists — it was likely created by an administrator. ' +
          'Use "Forgot password?" below to set your password and log in.'
        );
      } else {
        setError(err.message || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Student join — P2-S3: validates codes via Supabase RPC ──────────────
  const handleStudentJoin = async (e) => {
    e?.preventDefault();
    if (!idValue.trim() || !sessionCode.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await exerciseService.validateStudentJoin(
        sessionCode.trim(),
        idValue.trim()
      );
      const studentSessionData = {
        userName:         result.student_name,
        studentId:        result.student_id,
        exerciseId:       result.exercise_id,
        sessionCode:      sessionCode.trim().toUpperCase(),
        scenarioSnapshot: result.scenario_snapshot,
      };
      _saveStudentSession(studentSessionData); // SF-1: persist for refresh
      onResolved('STUDENT', studentSessionData);
    } catch (err) {
      setError(
        err.message?.includes('Invalid') || err.message?.includes('closed')
          ? 'Invalid personal code or session code. The room may be closed.'
          : (err.message || 'Could not join. Check your codes and try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  // ── P3: Student self-registration — no personal code needed ─────────────
  const handleSelfRegister = async (e) => {
    e?.preventDefault();
    if (!selfRegSessionCode.trim() || !selfRegName.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await exerciseService.selfRegisterStudent({
        sessionCode: selfRegSessionCode.trim(),
        name:        selfRegName.trim(),
        rank:        selfRegRank.trim(),
        country:     selfRegCountry.trim(),
      });
      setPendingStudentSession(result);
      setView(VIEW.STUDENT_CODE_ASSIGNED);
    } catch (err) {
      setError(
        err.message?.includes('closed') || err.message?.includes('Invalid')
          ? 'Invalid session code or the exercise is closed. Check with your trainer.'
          : (err.message || 'Registration failed. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot password — P2-S6: 24h delay via pending_resets table ──────────
  // First call: queues a row (execute_at = now+24h) → show RESET_PENDING notice.
  // Second call (after 24h): fires the actual Supabase reset email.
  const handleResetPassword = async (e) => {
    e?.preventDefault();
    const email = (resetEmail || idValue).trim();
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      const result = await authService.checkOrCreatePendingReset(email);
      if (result.status === 'ready') {
        // 24h has passed — fire the actual Supabase reset email now
        await authService.resetPassword(email);
        setView(VIEW.RESET_SENT);
      } else {
        // queued or pending — show the "return in X hours" screen
        setPendingReset({ id: result.id, hours_remaining: result.hours_remaining, email });
        setView(VIEW.RESET_PENDING);
      }
    } catch (err) {
      setError(err.message || 'Could not queue reset. Check the address and try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Cancel a queued reset (P2-S6) ────────────────────────────────────────
  const handleCancelReset = async () => {
    if (!pendingReset) return;
    setLoading(true);
    setError(null);
    try {
      await authService.cancelPendingReset(pendingReset.id, pendingReset.email);
      setPendingReset(null);
      setView(VIEW.TRAINER_LOGIN);
    } catch (err) {
      setError('Could not cancel reset. Try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Guest ─────────────────────────────────────────────────────────────────
  const handleGuest = () => {
    onResolved('GUEST', { userName: 'Guest' });
  };

  // ── Loading spinner (checking existing session) ───────────────────────────
  if (checkingSession) {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center',
        height: '100vh', gap: 2,
      }}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Checking session…
        </Typography>
      </Box>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      justifyContent: 'center', alignItems: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #01579b 100%)',
      p: 2,
    }}>
      <Paper elevation={8} sx={{
        width: '100%', maxWidth: 420,
        p: { xs: 3, sm: 4 },
        borderRadius: 3,
      }}>

        {/* ── Header ────────────────────────────────────────────────────── */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={700} color="primary.dark">
            PSP / CRE
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Planning Support Platform
          </Typography>
        </Box>

        {/* ── Admin invite welcome banner ───────────────────────────────── */}
        {isInvitedUser && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={700}>Account activated — welcome to PSP/CRE!</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Your trainer account is ready. Click below to continue.
              You can set a permanent password later from Account Settings.
            </Typography>
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={handleInvitedContinue}
              disabled={loading}
              sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
            >
              {loading ? 'Please wait…' : 'Continue to PSP/CRE →'}
            </Button>
          </Alert>
        )}

        {/* ── Co-organiser invite banner ────────────────────────────────── */}
        {!isInvitedUser && sessionStorage.getItem('psp_pending_invite') && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Exercise Invitation</strong>
            <br />
            You have been invited to co-organise an exercise.
            Log in or register below to accept.
          </Alert>
        )}

        {/* ── Error ─────────────────────────────────────────────────────── */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* ════════════════════════════════════════════════════════════════
            RESET SENT CONFIRMATION
        ════════════════════════════════════════════════════════════════ */}
        {view === VIEW.RESET_SENT ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>📧 Check your inbox</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A password reset link has been sent to{' '}
              <strong>{resetEmail || idValue}</strong>.
              <br />Click the link in the email to set a new password.
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 2 }}>
              The link expires in 1 hour. Check your spam folder if it does not arrive.
            </Typography>
            <Button
              variant="outlined" fullWidth
              onClick={() => {
                setView(VIEW.TRAINER_LOGIN);
                setError(null);
              }}
            >
              Back to Sign In
            </Button>
          </Box>

        ) : view === VIEW.RESET_PENDING ? (
        /* ════════════════════════════════════════════════════════════════
            P2-S6: RESET PENDING — return in 24h
        ════════════════════════════════════════════════════════════════ */
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>🕐 Reset queued</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              A 24-hour security delay applies to all password reset requests.
            </Typography>
            <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
              Return to this screen in approximately{' '}
              <strong>{pendingReset?.hours_remaining}h</strong> and go through
              "Forgot password?" again. The reset email will be sent immediately at
              that point.
            </Alert>
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 2 }}>
              This delay protects your account from unauthorised resets.
              If you did not request this, click below to cancel.
            </Typography>
            <Button
              variant="outlined" color="error" fullWidth sx={{ mb: 1 }}
              onClick={handleCancelReset}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Cancel Reset Request
            </Button>
            <Button
              variant="text" fullWidth
              onClick={() => { setView(VIEW.TRAINER_LOGIN); setError(null); }}
            >
              Back to sign in
            </Button>
          </Box>

        ) : view === VIEW.FORGOT_PASSWORD ? (
        /* ════════════════════════════════════════════════════════════════
            FORGOT PASSWORD FORM
        ════════════════════════════════════════════════════════════════ */
          <Box component="form" onSubmit={handleResetPassword}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
              Reset your password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your trainer email address. We will send you a link
              to set a new password. No admin involvement needed.
            </Typography>
            <TextField
              label="Email address" fullWidth size="small" sx={{ mb: 2 }}
              value={resetEmail}
              onChange={e => setResetEmail(e.target.value)}
              type="email" placeholder="yourname@gmail.com"
              required autoFocus
            />
            <Button
              type="submit" variant="contained" fullWidth
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
              disabled={loading || !resetEmail}
            >
              {loading ? 'Sending…' : 'Send Reset Link'}
            </Button>
            <Box sx={{ textAlign: 'center', mt: 1.5 }}>
              <Link
                component="button" variant="body2"
                onClick={() => { setView(VIEW.TRAINER_LOGIN); setError(null); }}
              >
                Back to sign in
              </Link>
            </Box>
          </Box>

        ) : view === VIEW.TRAINER_REG ? (
        /* ════════════════════════════════════════════════════════════════
            TRAINER REGISTRATION FORM
        ════════════════════════════════════════════════════════════════ */
          <Box component="form" onSubmit={handleRegister}>
            {trainerInviteValid ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                <strong>You've been invited as a trainer.</strong><br />
                Fill in your details below to create your account. Choose any email and password — you'll use them to sign in every time.
              </Alert>
            ) : (
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                Create trainer account
              </Typography>
            )}

            <TextField
              label="Full name" fullWidth size="small" sx={{ mb: 1.5 }}
              value={regName} onChange={e => setRegName(e.target.value)}
              placeholder="Dr. Tatar Radu" required autoFocus
            />
            <TextField
              label="Email address" fullWidth size="small" sx={{ mb: 1.5 }}
              value={regEmail} onChange={e => setRegEmail(e.target.value)}
              type="email" placeholder="yourname@gmail.com" required
              helperText="Use a personal email — not .gov or .mil"
            />
            <TextField
              label="Organisation" fullWidth size="small" sx={{ mb: 1.5 }}
              value={regOrg} onChange={e => setRegOrg(e.target.value)}
              placeholder="Romanian Land Forces Medical Service"
            />
            <TextField
              label="Password" fullWidth size="small" sx={{ mb: 1.5 }}
              value={regPassword} onChange={e => setRegPassword(e.target.value)}
              type={showPassword ? 'text' : 'password'} required
              helperText="Minimum 8 characters. Write it down — it cannot be recovered."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword(p => !p)}>
                      {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm password" fullWidth size="small" sx={{ mb: 2 }}
              value={regConfirm} onChange={e => setRegConfirm(e.target.value)}
              type={showPassword ? 'text' : 'password'} required
            />

            <Button
              type="submit" variant="contained" fullWidth
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
              disabled={loading || !regName || !regEmail || !regPassword || !regConfirm}
            >
              {loading ? 'Creating account…' : 'Create Account'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 1.5 }}>
              <Link
                component="button" variant="body2"
                onClick={() => { setView(VIEW.TRAINER_LOGIN); setError(null); }}
              >
                Already have an account? Sign in
              </Link>
            </Box>
          </Box>

        ) : view === VIEW.STUDENT_SELF_REGISTER ? (
        /* ════════════════════════════════════════════════════════════════
            P3: STUDENT SELF-REGISTRATION
            No pre-issued personal code — register with session code + name.
        ════════════════════════════════════════════════════════════════ */
          <Box component="form" onSubmit={handleSelfRegister}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
              Register to join exercise
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the session code your trainer provided and your details.
              You will be assigned a personal code — write it down for future sessions.
            </Typography>

            <TextField
              label="Session Code" fullWidth size="small" sx={{ mb: 1.5 }}
              value={selfRegSessionCode}
              onChange={e => setSelfRegSessionCode(e.target.value.toUpperCase())}
              placeholder="ROMEO-7"
              helperText="Provided by the exercise trainer"
              inputProps={{ style: { textTransform: 'uppercase', letterSpacing: 2 } }}
              required autoFocus
            />
            <TextField
              label="Full name" fullWidth size="small" sx={{ mb: 1.5 }}
              value={selfRegName}
              onChange={e => setSelfRegName(e.target.value)}
              placeholder="Cpt. Smith" required
            />
            <TextField
              label="Rank (optional)" fullWidth size="small" sx={{ mb: 1.5 }}
              value={selfRegRank}
              onChange={e => setSelfRegRank(e.target.value)}
              placeholder="Captain"
            />
            <TextField
              label="Country (optional)" fullWidth size="small" sx={{ mb: 2 }}
              value={selfRegCountry}
              onChange={e => setSelfRegCountry(e.target.value)}
              placeholder="ROU"
            />

            <Button
              type="submit" variant="contained" color="success" fullWidth
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SchoolIcon />}
              disabled={loading || !selfRegSessionCode || !selfRegName}
            >
              {loading ? 'Registering…' : 'Register & Join'}
            </Button>
            <Box sx={{ textAlign: 'center', mt: 1.5 }}>
              <Link
                component="button" variant="body2"
                onClick={() => { setView(VIEW.IDENTIFY); setError(null); }}
              >
                Back
              </Link>
            </Box>
          </Box>

        ) : view === VIEW.STUDENT_CODE_ASSIGNED ? (
        /* ════════════════════════════════════════════════════════════════
            P3: PERSONAL CODE ASSIGNED — confirm before entering exercise
        ════════════════════════════════════════════════════════════════ */
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>✅ Registered!</Typography>
            <Alert severity="success" sx={{ mb: 2, textAlign: 'left' }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Your personal code is:
              </Typography>
              <Typography
                variant="h5" fontWeight={700}
                sx={{ letterSpacing: 3, fontFamily: 'monospace', mb: 0.5 }}
              >
                {pendingStudentSession?.personal_code}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                ✏️ Write this down — you will need it to rejoin future sessions.
              </Typography>
            </Alert>
            <Button
              variant="contained" color="success" fullWidth
              startIcon={<SchoolIcon />}
              onClick={() => {
                const selfRegData = {
                  userName:         pendingStudentSession.student_name,
                  studentId:        pendingStudentSession.student_id,
                  exerciseId:       pendingStudentSession.exercise_id,
                  sessionCode:      selfRegSessionCode.trim().toUpperCase(),
                  scenarioSnapshot: pendingStudentSession.scenario_snapshot,
                };
                _saveStudentSession(selfRegData); // SF-1: persist for refresh
                onResolved('STUDENT', selfRegData);
              }}
            >
              Enter Exercise
            </Button>
          </Box>

        ) : (
          /* ════════════════════════════════════════════════════════════════
              MAIN IDENTIFY FORM (login + student + guest)
          ════════════════════════════════════════════════════════════════ */
          <Box>
            {/* Your ID field */}
            <TextField
              label="Your ID"
              fullWidth size="small" sx={{ mb: 0.5 }}
              value={idValue}
              onChange={handleIdChange}
              placeholder="email@example.com or ALPHA-12"
              autoFocus
              helperText={
                view === VIEW.TRAINER_LOGIN ? 'Trainer account detected' :
                view === VIEW.STUDENT_JOIN  ? 'Student code detected' :
                'Enter your trainer email or student code'
              }
              FormHelperTextProps={{
                sx: {
                  color:
                    view === VIEW.TRAINER_LOGIN ? 'primary.main' :
                    view === VIEW.STUDENT_JOIN  ? 'success.main' :
                    'text.secondary',
                }
              }}
            />

            {/* ── Trainer login section ──────────────────────────────── */}
            <Collapse in={view === VIEW.TRAINER_LOGIN} unmountOnExit>
              {/* TRAINER-GATE-001: invite banner — shown when a valid token was detected */}
              {trainerInviteChecking && (
                <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mb: 1.5 }}>
                  Validating trainer invite…
                </Alert>
              )}
              {!trainerInviteChecking && trainerInviteValid && (
                <Alert severity="success" sx={{ mb: 1.5 }}>
                  ✓ Valid trainer invite — create your account below, or sign in if you already have one.
                </Alert>
              )}
              {!trainerInviteChecking && trainerInviteToken && !trainerInviteValid && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  This trainer invite is invalid or has already been used. Contact your administrator for a new link.
                </Alert>
              )}
              <Box component="form" onSubmit={handleLogin}>
                <TextField
                  label="Password" fullWidth size="small" sx={{ mt: 1.5, mb: 1.5 }}
                  value={password} onChange={e => setPassword(e.target.value)}
                  type={showPassword ? 'text' : 'password'} required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword(p => !p)}>
                          {showPassword
                            ? <VisibilityOffIcon fontSize="small" />
                            : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit" variant="contained" fullWidth
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <LoginIcon />}
                  disabled={loading || !idValue || !password}
                >
                  {loading ? 'Signing in…' : 'Sign In as Trainer'}
                </Button>
                <Box sx={{ textAlign: 'center', mt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Link
                    component="button" variant="body2"
                    onClick={() => {
                      setResetEmail(idValue);
                      setView(VIEW.FORGOT_PASSWORD);
                      setError(null);
                    }}
                  >
                    Forgot password?
                  </Link>
                  {/* TRAINER-GATE-001: "Create account" only visible with a valid invite token */}
                  {(trainerInviteValid || trainerInviteChecking) ? (
                    <Link
                      component="button" variant="body2"
                      onClick={() => {
                        setRegEmail(idValue);
                        setView(VIEW.TRAINER_REG);
                        setError(null);
                      }}
                    >
                      No account yet? Create one
                    </Link>
                  ) : (
                    <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                      New accounts require an admin invite
                    </Typography>
                  )}
                </Box>
              </Box>
            </Collapse>

            {/* ── Student join section ───────────────────────────────── */}
            <Collapse in={view === VIEW.STUDENT_JOIN} unmountOnExit>
              <Box component="form" onSubmit={handleStudentJoin}>
                <TextField
                  label="Session Code"
                  fullWidth size="small" sx={{ mt: 1.5, mb: 1.5 }}
                  value={sessionCode}
                  onChange={e => setSessionCode(e.target.value.toUpperCase())}
                  placeholder="ROMEO-7"
                  helperText="Given to you by the exercise trainer"
                  inputProps={{ style: { textTransform: 'uppercase', letterSpacing: 2 } }}
                  required
                />
                <Button
                  type="submit" variant="contained" color="success" fullWidth
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SchoolIcon />}
                  disabled={!idValue || !sessionCode || loading}
                >
                  {loading ? 'Validating…' : 'Join Exercise'}
                </Button>
              </Box>
            </Collapse>

            {/* ── Divider ────────────────────────────────────────────── */}
            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" color="text.secondary">or</Typography>
            </Divider>

            {/* ── Guest button ───────────────────────────────────────── */}
            <Button
              variant="outlined" fullWidth color="inherit"
              onClick={handleGuest}
              sx={{ color: 'text.secondary', borderColor: 'divider' }}
            >
              Continue as Guest
            </Button>

            {/* ── Info chips ─────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mt: 2 }}>
              <Chip label="Trainer: email + password" size="small" variant="outlined" color="primary" sx={{ fontSize: '0.65rem' }} />
              <Chip label="Student: personal code" size="small" variant="outlined" color="success" sx={{ fontSize: '0.65rem' }} />
              <Chip label="Guest: learning mode" size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
            </Box>

            {/* ── Self-registration link ──────────────────────────── */}
            <Box sx={{ textAlign: 'center', mt: 1.5 }}>
              <Link
                component="button" variant="caption"
                color="text.secondary"
                onClick={() => { setView(VIEW.STUDENT_SELF_REGISTER); setError(null); }}
              >
                No personal code? Register with session code →
              </Link>
            </Box>
          </Box>
        )}

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <Typography
          variant="caption" color="text.disabled"
          display="block" textAlign="center" sx={{ mt: 3 }}
        >
          PSP/CRE · Planning Support Platform · UNCLASSIFIED
        </Typography>
      </Paper>
    </Box>
  );
}
